import {Address} from './client';
import {Domain} from './domain';
import {RecipientFee} from './fee';
import {LegalRepresentative} from './person';
import {Wallet} from './wallet';

export interface Recipient {
  id: number;
  name: string;
  document: string;
  identifier: string;
  domain: Domain;
  email: string;
  status: RecipientStatus;
  representative?: LegalRepresentative;
  createdAt: Date;
  provider: string;
  updatedAt: Date;
  type: string;
  documents: {
    id: number;
    fileUrl: string;
  }[];
  metadata?: RecipientMetadata;
  businessAreas: RecipientBusinessArea[];
  address: Address;
  logo: string;
  fees: RecipientFee[];
  wallet: Wallet;
  isLegalPerson: boolean;
  officialData: RecipientOfficialData;
  standalone: boolean;
  sandbox?: boolean;
  website?: string;
}

export enum RecipientBusinessArea {
  ECOMMERCE = 'ecommerce',
  FOOD_DELIVERY = 'food_delivery',
  HEALTHCARE = 'healthcare',
  TECHNOLOGY = 'technology',
  RETAIL = 'retail',
  EDUCATION = 'education',
  FINANCE = 'finance',
  TRANSPORTATION = 'transportation',
  ENTERTAINMENT = 'entertainment',
  REAL_ESTATE = 'real_estate',
  HOSPITALITY = 'hospitality',
  AGRICULTURE = 'agriculture',
  ONLINE_BETTING = 'online_betting',
  DROPSHIPPING = 'dropshipping',
  AFFILIATE_MARKETING = 'affiliate_marketing',
  DIGITAL_AGENCY = 'digital_agency',
  SOFTWARE_DEVELOPMENT = 'software_development',
}

export const recipientBusinessAreaOptions = [
  {value: RecipientBusinessArea.ECOMMERCE, label: 'E-commerce'},
  {value: RecipientBusinessArea.FOOD_DELIVERY, label: 'Entrega de Alimentos'},
  {value: RecipientBusinessArea.HEALTHCARE, label: 'Saúde'},
  {value: RecipientBusinessArea.TECHNOLOGY, label: 'Tecnologia'},
  {value: RecipientBusinessArea.RETAIL, label: 'Varejo'},
  {value: RecipientBusinessArea.EDUCATION, label: 'Educação'},
  {value: RecipientBusinessArea.FINANCE, label: 'Financeiro'},
  {value: RecipientBusinessArea.TRANSPORTATION, label: 'Transporte'},
  {value: RecipientBusinessArea.ENTERTAINMENT, label: 'Entretenimento'},
  {value: RecipientBusinessArea.REAL_ESTATE, label: 'Imobiliário'},
  {value: RecipientBusinessArea.HOSPITALITY, label: 'Hotelaria'},
  {value: RecipientBusinessArea.AGRICULTURE, label: 'Agricultura'},
  {value: RecipientBusinessArea.ONLINE_BETTING, label: 'Apostas Online'},
  {value: RecipientBusinessArea.DROPSHIPPING, label: 'Dropshipping'},
  {
    value: RecipientBusinessArea.AFFILIATE_MARKETING,
    label: 'Marketing de Afiliados',
  },
  {value: RecipientBusinessArea.DIGITAL_AGENCY, label: 'Agência Digital'},
  {
    value: RecipientBusinessArea.SOFTWARE_DEVELOPMENT,
    label: 'Desenvolvimento de Software',
  },
];

export enum RecipientStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING_COMPLIANCE = 'pending_compliance',
  FAILED_COMPLIANCE = 'failed_compliance',
  SUSPENDED = 'suspended',
  REGISTRATION_INCOMPLETE = 'registration_incomplete',
}

export interface RecipientMetadata {
  riskIndex?: number;
  ipAddress?: string;
  userAgent?: string;
}

export interface RecipientKey {
  id: number;
  key: string;
  publicKey?: string;
  secret: string;
  enabled: boolean;
  allowList: string[];
  recipient?: Recipient;
  domain?: Domain;
  description?: string;
  createdAt: Date;
  updatedAt: Date;
  lastActivity?: {
    createdAt: Date;
  };
}

export interface RecipientOfficialData {
  updated: string;
  taxId: string;
  alias: string;
  founded: string;
  head: boolean;
  company: Company;
  statusDate: string;
  status: OfficialDataStatus;
  address: OfficialDataAddress;
  mainActivity: MainActivity;
  phones: Phone[];
  emails: Email[];
  sideActivities: SideActivity[];
  registrations: unknown[];
  suframa: unknown[];
}

export interface Company {
  members: Member[];
  id: number;
  name: string;
  equity: number;
  nature: Nature;
  size: Size;
  simples: Simples;
  simei: Simei;
}

export interface Member {
  since: string;
  person: Person;
  role: Role;
}

export interface Person {
  id: string;
  type: string;
  name: string;
  taxId: string;
  age: string;
}

export interface Role {
  id: number;
  text: string;
}

export interface Nature {
  id: number;
  text: string;
}

export interface Size {
  id: number;
  acronym: string;
  text: string;
}

export interface Simples {
  optant: boolean;
  since: string;
}

export interface Simei {
  optant: boolean;
  since: unknown;
}

export interface OfficialDataStatus {
  id: number;
  text: string;
}

export interface OfficialDataAddress {
  municipality: number;
  street: string;
  number: string;
  district: string;
  city: string;
  state: string;
  details: unknown;
  zip: string;
  country: Country;
}

export interface Country {
  id: number;
  name: string;
}

export interface MainActivity {
  id: number;
  text: string;
}

export interface Phone {
  type: string;
  area: string;
  number: string;
}

export interface Email {
  ownership: string;
  address: string;
  domain: string;
}

export interface SideActivity {
  id: number;
  text: string;
}

export {RecipientSettingKey, PayoutPolicy} from './recipient-setting';

export enum RecipientType {
  TRANSACTIONAL = 'transactional',
  CAPITALIZATION = 'capitalization',
}

export interface RecipientBankAccount {
  id: number;
  bankCode: string;
  agency: string;
  account: string;
  type: string;
  status: string;
  createdAt: Date;
  updatedAt: Date;
}

/** Payload for domain recipient creation (includes optional integration credentials). */
export type CreateRecipientPayload = Partial<Recipient> & {
  credentials?: Record<string, unknown>;
};
